import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, HelpCircle, LogOut, User, Users } from "lucide-react";
import type { AppShellHeaderMenuItem } from "@musa.tech/design-system";
import { useAuthContext } from "@/components/AuthProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { isEmbedMode } from "@/embed/host";

export interface BuildHeaderMenuItemsParams {
  canManageUsers: boolean;
  /** Embutido em outro portal — ver o porquê na montagem do item "Sair". */
  isEmbedded: boolean;
  canUseDemoMode: boolean;
  isDemoMode: boolean;
  navigate: (path: string) => void;
  onToggleDemoMode: () => void;
  onSignOut: () => void;
  onTrackUsuariosClick: () => void;
  onTrackCentralAjudaClick: () => void;
}

/**
 * Monta os itens do menu da engrenagem, extraída à parte pra testar sem
 * precisar montar o hook (mesmo padrão de `buildBreadcrumb` em
 * useBreadcrumb.ts). "Usuários" substitui "Configurações" (GR-668:
 * engrenagem > Usuários direto, sem passar pela tela de Configurações) e só
 * aparece pra quem pode gerenciar usuários — mesma regra do card em
 * Configuracoes.tsx.
 */
export const buildHeaderMenuItems = ({
  canManageUsers,
  isEmbedded,
  canUseDemoMode,
  isDemoMode,
  navigate,
  onToggleDemoMode,
  onSignOut,
  onTrackUsuariosClick,
  onTrackCentralAjudaClick,
}: BuildHeaderMenuItemsParams): AppShellHeaderMenuItem[] => {
  const items: AppShellHeaderMenuItem[] = [
    {
      id: "minha-conta",
      label: "Minha conta",
      icon: User,
      onSelect: () => navigate("/meu-perfil"),
    },
  ];

  if (canManageUsers) {
    items.push({
      id: "usuarios",
      label: "Usuários",
      icon: Users,
      onSelect: () => {
        onTrackUsuariosClick();
        navigate("/usuarios");
      },
    });
  }

  if (canUseDemoMode) {
    items.push({
      id: "modo-demonstracao",
      // Rótulo fixo — o ícone (Eye/EyeOff) já indica o estado atual.
      label: "Modo demo",
      icon: isDemoMode ? EyeOff : Eye,
      onSelect: onToggleDemoMode,
    });
  }

  items.push({
    id: "central-de-ajuda",
    label: "Central de ajuda",
    icon: HelpCircle,
    onSelect: () => {
      onTrackCentralAjudaClick();
      navigate("/ajuda");
    },
  });

  // Embutido, quem desloga é o portal que embute: o "Sair" dele fica no rodapé
  // da sidebar, na mesma tela que este menu. Dois botões de sair visíveis ao
  // mesmo tempo, um dentro do iframe e outro fora, confundiam — e o de fora é o
  // que tem a sessão do portal e alcança o `end_session` do Keycloak.
  if (!isEmbedded) {
    items.push({
      id: "sair",
      label: "Sair",
      icon: LogOut,
      onSelect: onSignOut,
    });
  }

  return items;
};

/**
 * Itens do menu da engrenagem no header. Quem pode usar o Modo Demonstração é
 * decidido pelo DemoModeProvider — aqui só se pergunta.
 */
export const useHeaderMenuItems = (): AppShellHeaderMenuItem[] => {
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const { isDemoMode, canUseDemoMode, toggleDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();
  const { canManageUsers } = usePermissions();

  return useMemo(() => {
    const handleSignOut = async () => {
      try {
        await signOut();
      } catch {
        window.location.href = "/login";
      }
    };

    return buildHeaderMenuItems({
      canManageUsers,
      isEmbedded: isEmbedMode(),
      canUseDemoMode,
      isDemoMode,
      navigate,
      onToggleDemoMode: toggleDemoMode,
      onSignOut: handleSignOut,
      onTrackUsuariosClick: () =>
        trackButtonClick({ buttonLabel: "menu_click_usuarios" }),
      onTrackCentralAjudaClick: () =>
        trackButtonClick({ buttonLabel: "menu_click_central_ajuda" }),
    });
  }, [
    canManageUsers,
    canUseDemoMode,
    isDemoMode,
    navigate,
    signOut,
    toggleDemoMode,
    trackButtonClick,
  ]);
};
